import { useEffect, useRef, useCallback } from 'react';
import { usePublicConfig } from '@/core/hooks/usePublicConfig';
import { useAuth } from '@/core/contexts/AuthContext';
import { getOrCreateClientId } from '@/core/identity/identity';
// Use generated Orval function for anonymous profile update
import { anonymousUpdateProfile } from '@/core/api/generated/anonymous/anonymous';

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => void>;
    OneSignal?: any;
    __oneSignalInitialized?: boolean;
  }
}

/**
 * OneSignalInitializer component
 * 
 * Automatically initializes OneSignal when:
 * 1. Public config is loaded
 * 2. OneSignal App ID is configured
 * 
 * Handles:
 * - OneSignal SDK initialization
 * - Device token registration (only when user is authenticated)
 * - Permission requests
 * 
 * Note: Uses OneSignal Web SDK v16 API
 * @see https://documentation.onesignal.com/docs/web-sdk-reference
 */
export function OneSignalInitializer() {
  const { config } = usePublicConfig();
  const { user } = useAuth();
  const tokenRegisteredRef = useRef(false);
  const initStartedRef = useRef(false);
  const userRef = useRef(user);

  // Keep userRef in sync with user
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Helper function to get push subscription ID (OneSignal v16 API)
  const getPlayerId = useCallback(async (): Promise<string | null> => {
    try {
      const OneSignal = window.OneSignal;
      if (!OneSignal) return null;

      // v16 API: User.PushSubscription.id
      const subscriptionId = OneSignal.User?.PushSubscription?.id;
      if (subscriptionId) {
        console.log('[OneSignal] Got subscription ID:', subscriptionId);
        return subscriptionId;
      }
      
      // Fallback: try legacy method if available
      if (typeof OneSignal.getUserId === 'function') {
        const legacyId = await OneSignal.getUserId();
        console.log('[OneSignal] Got legacy user ID:', legacyId);
        return legacyId;
      }
      
      console.log('[OneSignal] No subscription ID available yet');
      return null;
    } catch (error) {
      console.error('[OneSignal] Failed to get player ID:', error);
      return null;
    }
  }, []);

  // Helper function to check if notifications are enabled (OneSignal v16 API)
  const isNotificationsEnabled = useCallback((): boolean => {
    try {
      const OneSignal = window.OneSignal;
      if (!OneSignal) return false;
      
      // v16 API: Notifications.permission returns boolean
      const permission = OneSignal.Notifications?.permission;
      console.log('[OneSignal] Current permission:', permission);
      return permission === true;
    } catch {
      return false;
    }
  }, []);

  // Helper function to register token - uses direct API call instead of hook
  const registerToken = useCallback(async () => {
    // Use ref to get current user value
    const currentUser = userRef.current;
    
    console.log('[OneSignal] Attempting to register token. User:', currentUser?.id || 'none', 'Already registered:', tokenRegisteredRef.current);
    
    if (tokenRegisteredRef.current) {
      console.log('[OneSignal] Token already registered');
      return;
    }
    
    try {
      const playerId = await getPlayerId();
      console.log('[OneSignal] Player ID for registration:', playerId);
      
      if (!playerId) {
        console.log('[OneSignal] No player ID available, cannot register');
        return;
      }

      // === G5: Register for both authenticated and anonymous users ===
      if (currentUser) {
        // Authenticated user: save to UserDeviceToken
        const platform = /iPhone|iPad|iPod/.test(navigator.userAgent)
          ? 'ios'
          : /Android/.test(navigator.userAgent)
          ? 'android'
          : 'web';

        const deviceInfo = `${navigator.userAgent} - ${navigator.platform}`;

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.log('[OneSignal] No access token, cannot register for user');
          return;
        }

        console.log('[OneSignal] Sending token to backend for authenticated user...');
        const response = await fetch('/api/user/device-tokens', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            token: playerId,
            platform,
            deviceInfo,
          }),
        });
        
        if (response.ok) {
          tokenRegisteredRef.current = true;
          console.log('[OneSignal] ✅ Device token registered for user!');
        } else {
          const errorText = await response.text();
          console.error('[OneSignal] ❌ Failed to register token:', response.status, errorText);
        }
      } else {
        // Anonymous user: save to AnonymousProfile
        const clientId = getOrCreateClientId();
        console.log('[OneSignal] Sending token to backend for anonymous user...');
        
        try {
          // Use generated Orval function
          await anonymousUpdateProfile({
            clientId,
            onesignalPlayerId: playerId,
          });
          tokenRegisteredRef.current = true;
          console.log('[OneSignal] ✅ OneSignal playerId saved to AnonymousProfile!');
        } catch (error) {
          console.error('[OneSignal] ❌ Failed to save playerId to AnonymousProfile:', error);
        }
      }
    } catch (error) {
      console.error('[OneSignal] ❌ Failed to register token:', error);
    }
  }, [getPlayerId]);

  // Request notification permission (OneSignal v16 API)
  const requestPermission = useCallback(async () => {
    try {
      const OneSignal = window.OneSignal;
      if (!OneSignal) return;

      // Check current permission state
      const currentPermission = Notification?.permission;
      console.log('[OneSignal] Current browser permission:', currentPermission);

      // If browser permission is already granted, just register token
      if (currentPermission === 'granted') {
        console.log('[OneSignal] Browser permission already granted');
        await registerToken();
        return;
      }

      // If browser permission is denied, we can't do anything
      if (currentPermission === 'denied') {
        console.log('[OneSignal] Browser permission denied - user must enable manually');
        return;
      }

      // Try native prompt first (more reliable)
      if (OneSignal.Notifications?.requestPermission) {
        console.log('[OneSignal] Requesting native permission');
        const permission = await OneSignal.Notifications.requestPermission();
        console.log('[OneSignal] Permission result:', permission);
        
        if (permission) {
          // Permission granted, register token after a short delay
          setTimeout(() => {
            registerToken();
          }, 1000);
        }
      } else if (OneSignal.Slidedown?.promptPush) {
        // Fallback to slidedown (soft prompt)
        console.log('[OneSignal] Showing slidedown prompt');
        await OneSignal.Slidedown.promptPush();
      }
    } catch (error) {
      console.log('[OneSignal] Permission request failed or dismissed:', error);
    }
  }, [registerToken]);

  // Initialize OneSignal
  useEffect(() => {
    // Skip if already started initialization or no config
    if (initStartedRef.current || window.__oneSignalInitialized) {
      return;
    }

    // Wait for config to load
    if (!config || !config.onesignalAppId) {
      return;
    }

    // Mark as started to prevent double initialization
    initStartedRef.current = true;
    
    console.log('[OneSignal] Starting initialization with App ID:', config.onesignalAppId);

    // Initialize OneSignal using deferred loading pattern
    if (!window.OneSignalDeferred) {
      window.OneSignalDeferred = [];
    }

    // Only push if we haven't initialized yet
    if (!window.__oneSignalInitialized) {
      window.OneSignalDeferred.push(async function(OneSignal: any) {
        // Double-check we haven't initialized
        if (window.__oneSignalInitialized) {
          console.log('[OneSignal] Already initialized, skipping');
          return;
        }

        try {
          console.log('[OneSignal] Executing initialization');
          
          await OneSignal.init({
            appId: config.onesignalAppId!,
            allowLocalhostAsSecureOrigin: true,
            notifyButton: {
              enable: false,
            },
            welcomeNotification: {
              disable: false,
            },
          });

          // Mark as initialized globally
          window.__oneSignalInitialized = true;
          window.OneSignal = OneSignal;
          
          console.log('[OneSignal] ✅ Initialization successful');

          // Listen for subscription changes (v16 API)
          if (OneSignal.User?.PushSubscription?.addEventListener) {
            OneSignal.User.PushSubscription.addEventListener('change', async (event: any) => {
              console.log('[OneSignal] 🔔 Subscription changed:', event);
              if (event.current?.optedIn) {
                console.log('[OneSignal] User opted in! Registering token...');
                // Small delay to ensure the subscription ID is available
                setTimeout(() => {
                  registerToken();
                }, 500);
              }
            });
            console.log('[OneSignal] Subscription change listener registered');
          }

          // Check if user is already subscribed
          const isSubscribed = OneSignal.Notifications?.permission === true;
          const browserPermission = Notification?.permission;
          console.log('[OneSignal] Is already subscribed:', isSubscribed);
          console.log('[OneSignal] Browser permission:', browserPermission);
          
          if (isSubscribed || browserPermission === 'granted') {
            // User already subscribed, register token
            console.log('[OneSignal] Already has permission, registering token...');
            await registerToken();
          } else if (browserPermission === 'denied') {
            // Permission is blocked, user must enable manually
            console.log('[OneSignal] Permission blocked - user must enable in browser settings');
          } else {
            // Permission is 'default' (not decided yet)
            // Use slidedown which is less intrusive and works without user gesture
            console.log('[OneSignal] Will show slidedown when user interacts');
            // Don't auto-request - wait for user interaction via slidedown
            if (OneSignal.Slidedown?.promptPush) {
              setTimeout(() => {
                OneSignal.Slidedown.promptPush().catch(() => {
                  console.log('[OneSignal] Slidedown dismissed or failed');
                });
              }, 3000);
            }
          }

        } catch (error: any) {
          // Ignore "already initialized" errors
          if (error?.message?.includes('already initialized')) {
            console.log('[OneSignal] SDK was already initialized');
            window.__oneSignalInitialized = true;
          } else {
            console.error('[OneSignal] Failed to initialize:', error);
          }
        }
      });
    }

    // Load OneSignal SDK script if not already loaded
    if (!document.querySelector('script[src*="OneSignalSDK.page.js"]')) {
      console.log('[OneSignal] Loading SDK script');
      const script = document.createElement('script');
      script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [config, registerToken, requestPermission]);

  // Track previous user to detect login transitions
  const prevUserRef = useRef<typeof user>(null);

  // Register token when user logs in (if already subscribed)
  useEffect(() => {
    if (!window.__oneSignalInitialized || !window.OneSignal) {
      prevUserRef.current = user;
      return;
    }

    // Detect transition from anonymous to authenticated
    const wasAnonymous = !prevUserRef.current;
    const isNowAuthenticated = !!user;
    const userJustLoggedIn = wasAnonymous && isNowAuthenticated;
    
    // Update previous user ref
    prevUserRef.current = user;

    if (!user) {
      return;
    }

    // If user just logged in, always re-register the token for the authenticated user
    // This ensures multi-device support works correctly
    if (userJustLoggedIn) {
      console.log('[OneSignal] User just logged in, re-registering token for authenticated user...');
      tokenRegisteredRef.current = false; // Reset so we can re-register
    }

    if (tokenRegisteredRef.current) {
      return;
    }

    console.log('[OneSignal] Checking if we should register token for user...');

    // User just logged in, try to register their token
    const tryRegister = async () => {
      if (isNotificationsEnabled()) {
        console.log('[OneSignal] Notifications enabled, registering token for logged in user');
        await registerToken();
      } else {
        console.log('[OneSignal] Notifications not enabled yet');
      }
    };

    // Small delay to ensure everything is ready
    const timer = setTimeout(tryRegister, 500);
    return () => clearTimeout(timer);
  }, [user, isNotificationsEnabled, registerToken]);

  return null;
}
